import * as React from "react";
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useTranslation} from "react-i18next";
import StatusChip from "./StatusChip";
import WorkEvidenceApprovalTableComponentMobile from "./mobile/WorkEvidenceApprovalTableComponentMobile";
import {getAvatarColors, getInitials} from "../../utils/avatarUtils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkEvidenceRecord {
    workEvidenceId: string | null;
    employeeId: string;
    firstName: string;
    lastName: string;
    /** Branch/tenant name, shown under the employee's name (reference: "Punonjësi / Dega") -
     *  relevant since this page can list records across all branches at once. Optional/nullable
     *  since not every backend response includes it. */
    branchName?: string | null;
    regularHours: number | null;
    overTimeHours: number | null;
    nightHours: number | null;
    weekendHours: number | null;
    holidayHours: number | null;
    status: string | null;
    comment: string | null;
}

export type Order = "asc" | "desc";
export type SortableKey =
    | "firstName"
    | "lastName"
    | "regularHours"
    | "overTimeHours"
    | "nightHours"
    | "weekendHours"
    | "holidayHours";

// ─── Sorting helpers ──────────────────────────────────────────────────────────

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

export function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// ─── Empty placeholder ────────────────────────────────────────────────────────

function EmptyPlaceholder({detail}: { detail: string }): React.ReactElement {
    const {t} = useTranslation();
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
                gap: 1,
            }}
        >
            <ManageHistoryIcon sx={{fontSize: 48, color: "text.disabled"}}/>
            <Typography variant="body1" color="text.secondary">{t('workEvidence.approval.empty.title')}</Typography>
            <Typography variant="body2" color="text.disabled" sx={{textAlign: "center"}}>{detail}</Typography>
        </Box>
    );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkEvidenceApprovalTableComponentProps {
    selectedPeriod: string | null;
    selectedBranch: string | null;
    isLoadingData: boolean;
    tableDataError: string | null;
    workEvidenceData: WorkEvidenceRecord[];
    filteredData: WorkEvidenceRecord[];
    currentPage: number;
    itemsPerPage: number;
    isPeriodLocked: boolean;
    canApprove: boolean;
    selected: string[];
    approvingId: string | null;
    rejectingId: string | null;
    isBulkLoading: boolean;
    isSelected: (id: string) => boolean;
    handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleRowCheckboxClick: (event: React.MouseEvent, id: string) => void;
    handleApprove: (record: WorkEvidenceRecord) => void;
    handleReject: (record: WorkEvidenceRecord) => void;
    handleBulkApprove: () => void;
    handleBulkReject: () => void;
    order: Order;
    orderBy: SortableKey;
    handleRequestSort: (property: SortableKey) => void;
    onPageChange: (page: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkEvidenceApprovalTableComponent({
                                                               selectedPeriod,
                                                               selectedBranch,
                                                               isLoadingData,
                                                               tableDataError,
                                                               workEvidenceData,
                                                               filteredData,
                                                               currentPage,
                                                               itemsPerPage,
                                                               isPeriodLocked,
                                                               canApprove,
                                                               selected,
                                                               approvingId,
                                                               rejectingId,
                                                               isBulkLoading,
                                                               isSelected,
                                                               handleSelectAllClick,
                                                               handleRowCheckboxClick,
                                                               handleApprove,
                                                               handleReject,
                                                               handleBulkApprove,
                                                               handleBulkReject,
                                                               order,
                                                               orderBy,
                                                               handleRequestSort,
                                                               onPageChange,
                                                           }: WorkEvidenceApprovalTableComponentProps): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Internal menu state
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [menuRowId, setMenuRowId] = React.useState<string | null>(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const handleMenuAction = (action: () => void) => {
        action();
        handleMenuClose();
    };

    // ─── Guard states ─────────────────────────────────────────────────────────

    if (!selectedPeriod || !selectedBranch) {
        return <EmptyPlaceholder detail={t('workEvidence.approval.empty.selectPeriodBranch')}/>;
    }

    if (isLoadingData) {
        return isMobile ? (
            <WorkEvidenceApprovalTableComponentMobile
                filteredData={[]}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                canApprove={canApprove}
                isPeriodLocked={isPeriodLocked}
                selected={selected}
                approvingId={approvingId}
                rejectingId={rejectingId}
                isSelected={isSelected}
                handleRowCheckboxClick={handleRowCheckboxClick}
                handleApprove={handleApprove}
                handleReject={handleReject}
                isLoading
            />
        ) : (
            <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (tableDataError) {
        return (
            <Alert severity="error" sx={{mt: 2}}>
                <AlertTitle>{t('common.error.title')}</AlertTitle>
                {tableDataError}
            </Alert>
        );
    }

    if (workEvidenceData.length === 0) {
        return <EmptyPlaceholder detail={t('workEvidence.approval.empty.selectAnother')}/>;
    }

    if (filteredData.length === 0) {
        return <EmptyPlaceholder detail={t('workEvidence.approval.empty.noResults')}/>;
    }

    const pageRows = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
    );
    const allPageSelected =
        pageRows.length > 0 && pageRows.every((r) => selected.includes(r.employeeId));
    const somePageSelected =
        pageRows.some((r) => selected.includes(r.employeeId)) && !allPageSelected;

    const countLabel = selected.length > 0 ? ` (${selected.length})` : "";
    const approveLabel = isBulkLoading ? t('common.processing') : t('common.actions.approve') + countLabel;
    const rejectLabel = isBulkLoading ? t('common.processing') : t('common.actions.reject') + countLabel;

    // ─── Mobile ───────────────────────────────────────────────────────────────

    if (isMobile) {
        return (
            <>
                <WorkEvidenceApprovalTableComponentMobile
                    filteredData={filteredData}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    canApprove={canApprove}
                    isPeriodLocked={isPeriodLocked}
                    selected={selected}
                    approvingId={approvingId}
                    rejectingId={rejectingId}
                    isSelected={isSelected}
                    handleRowCheckboxClick={handleRowCheckboxClick}
                    handleApprove={handleApprove}
                    handleReject={handleReject}
                />

                <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                    <Pagination
                        count={Math.ceil(filteredData.length / itemsPerPage)}
                        color="primary"
                        page={currentPage}
                        onChange={(_event, page) => onPageChange(page)}
                    />
                </Box>

                {canApprove && !isPeriodLocked && (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 3}}>
                        <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            startIcon={<CheckCircleOutlineIcon/>}
                            disabled={selected.length === 0 || isBulkLoading}
                            onClick={handleBulkApprove}
                        >
                            {approveLabel}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<CancelOutlinedIcon/>}
                            disabled={selected.length === 0 || isBulkLoading}
                            onClick={handleBulkReject}
                        >
                            {rejectLabel}
                        </Button>
                    </Box>
                )}
            </>
        );
    }

    // ─── Desktop ──────────────────────────────────────────────────────────────

    return (
        <>
            <TableContainer component={Paper} sx={{mt: 2, overflowX: "auto"}}>
                <Table sx={{minWidth: {xs: 500, md: 640}}}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    indeterminate={somePageSelected}
                                    checked={allPageSelected}
                                    onChange={handleSelectAllClick}
                                    inputProps={{"aria-label": t('workEvidence.approval.aria.selectAllRecords')}}
                                />
                            </TableCell>
                            <TableCell sortDirection={orderBy === "firstName" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "firstName"}
                                    direction={orderBy === "firstName" ? order : "asc"}
                                    onClick={() => handleRequestSort("firstName")}
                                >
                                    {t('workEvidence.approval.table.header.employeeBranch')}
                                </TableSortLabel>
                            </TableCell>
                            {(
                                [
                                    {label: t('workEvidence.table.header.regularHours'), field: "regularHours"},
                                    {label: t('workEvidence.table.header.overtimeHours'), field: "overTimeHours"},
                                ] as { label: string; field: SortableKey }[]
                            ).map(({label, field}) => (
                                <TableCell key={field} sortDirection={orderBy === field ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === field}
                                        direction={orderBy === field ? order : "asc"}
                                        onClick={() => handleRequestSort(field)}
                                    >
                                        {label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell>{t('hr.branches.table.header.status')}</TableCell>
                            <TableCell align="center">{t('workEvidence.approval.table.header.action')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((record) => {
                            const rowSelected = isSelected(record.employeeId);
                            const isApproved = record.status?.toUpperCase().includes("APPROVED");
                            const isRejected = record.status?.toUpperCase().includes("REJECTED");
                            const actionDisabled = !canApprove || isPeriodLocked || Boolean(isApproved);
                            const avatarColors = getAvatarColors(`${record.firstName}${record.lastName}${record.employeeId}`);

                            return (
                                <TableRow
                                    key={record.employeeId}
                                    hover
                                    selected={rowSelected}
                                    sx={{cursor: "pointer"}}
                                >
                                    <TableCell padding="checkbox"
                                               onClick={(e) => handleRowCheckboxClick(e, record.employeeId)}>
                                        <Checkbox
                                            color="primary"
                                            checked={rowSelected}
                                            inputProps={{"aria-label": t('workEvidence.approval.aria.selectRecord', {firstName: record.firstName, lastName: record.lastName})}}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                            <Avatar sx={{
                                                width: 36,
                                                height: 36,
                                                bgcolor: avatarColors.bg,
                                                color: avatarColors.color,
                                                fontSize: "0.875rem",
                                                fontWeight: 600,
                                                flexShrink: 0,
                                            }}>
                                                {getInitials(record.firstName, record.lastName)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {record.firstName} {record.lastName}
                                                </Typography>
                                                {record.branchName && (
                                                    <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>
                                                        {record.branchName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell><Typography
                                        variant="body2">{record.regularHours ?? "-"}</Typography></TableCell>
                                    <TableCell><Typography
                                        variant="body2">{record.overTimeHours ?? "-"}</Typography></TableCell>
                                    <TableCell>
                                        <StatusChip status={record.status}/>
                                    </TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                        <IconButton
                                            aria-label="more"
                                            disabled={actionDisabled}
                                            onClick={(e) => handleMenuClick(e, record.employeeId)}
                                        >
                                            <MoreVertIcon/>
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={menuOpen && menuRowId === record.employeeId}
                                            onClose={handleMenuClose}
                                            slotProps={{paper: {style: {maxHeight: 48 * 4.5, width: "18ch"}}}}
                                        >
                                            <MenuItem
                                                onClick={() => handleMenuAction(() => handleApprove(record))}
                                                disabled={Boolean(isApproved) || approvingId === record.employeeId}
                                            >
                                                <ListItemIcon>
                                                    <CheckCircleOutlineIcon fontSize="small" color="success"/>
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {approvingId === record.employeeId ? t('workEvidence.approval.approving') : t('common.actions.approve')}
                                                </ListItemText>
                                            </MenuItem>
                                            <Divider/>
                                            <MenuItem
                                                onClick={() => handleMenuAction(() => handleReject(record))}
                                                disabled={Boolean(isRejected) || rejectingId === record.employeeId}
                                            >
                                                <ListItemIcon>
                                                    <CancelOutlinedIcon fontSize="small" color="error"/>
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {rejectingId === record.employeeId ? t('workEvidence.approval.rejecting') : t('common.actions.reject')}
                                                </ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                <Pagination
                    count={Math.ceil(filteredData.length / itemsPerPage)}
                    color="primary"
                    page={currentPage}
                    onChange={(_event, page) => onPageChange(page)}
                />
            </Box>
        </>
    );
}
